import type { SxProps } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { kebabCase } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompletionBox } from '../completionBox/completionBox';
import { useSelectionTileTokens } from './selectionTile.tokens';

type SelectionTileProps = {
  direction?: 'row' | 'column';
  type?: 'checkbox' | 'radio' | 'tile' | 'completion';
  value?: string | number;
  options: Array<{
    label: string;
    subtitle?: string;
    value: string | number;
    labelSideComponent?: React.ReactNode;
  }>;
  onChange: (event: any) => void;
  error?: boolean;
  errorText?: string;
  component?: 'SUGGESTED' | 'OTHER' | 'DEFAULT';
  selectedComponent?: 'SUGGESTED' | 'OTHER' | 'DEFAULT';
  infoTooltip?: string;
  testId?: string;
  label?: string;
  disabled?: boolean;
  xs?: number;
  sx?: SxProps;
  innerSx?: SxProps;
};

export const SelectionTile = ({
  direction = 'column',
  type = 'radio',
  value,
  infoTooltip,
  options,
  error,
  errorText,
  component,
  selectedComponent,
  testId,
  label,
  disabled = false,
  xs,
  sx,
  innerSx,
  ...restProps
}: SelectionTileProps) => {
  const { sys } = useThemeTokens();
  const { comp } = useThemeTokens(useSelectionTileTokens());
  const { t } = useTranslation('components');
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const boxStyle: SxProps = {
    border: `1px solid ${comp.selectionTile.borderColor}`,
    borderRadius: comp.selectionTile.borderRadius,
    px: '12px',
    py: '2px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: comp.selectionTile.hoverBorderColor,
      backgroundColor: comp.selectionTile.hoverBackgroundColor,
    },
  };

  const [changing, setChanging] = useState(false);
  const onChange = async (event: any) => {
    setChanging(true);
    await restProps.onChange(event);
    setChanging(false);
  };

  if (type === 'radio') {
    return (
      <>
        {error && (
          <HelpText
            text={errorText ?? t('fieldErrors.selectionTile')}
            tone='error'
          />
        )}
        <RadioGroup
          testId={testId}
          value={value}
          infoTooltip={infoTooltip}
          label={label}
          onChange={onChange}
          fullWidth
          disabled={disabled || changing}
          sx={sx}
        >
          <Grid
            container
            direction={direction}
            spacing={2}
            width='100%'
            sx={{ flexGrow: 1 }}
          >
            {(options || []).map((option, index) => (
              <Grid
                data-testid={
                  testId
                    ? `${testId}-${kebabCase(option.label)}`
                    : `option-${index}`
                }
                item
                md
                xs={xs}
                key={index}
              >
                <Box
                  sx={boxStyle}
                  onClick={() => onChange({ target: { value: option.value } })}
                >
                  <Radio
                    testId={
                      testId
                        ? `${testId}-${kebabCase(option.label)}-radio`
                        : `radio-${index}`
                    }
                    disabled={disabled}
                    value={option.value}
                    label={
                      <Box display='flex' alignItems='center' gap={1}>
                        {option.label}
                        {option.labelSideComponent}
                      </Box>
                    }
                    size='small'
                  />
                  {option.subtitle && (
                    <Typography
                      data-testid='subtitle'
                      variant='bodySmall'
                      sx={{
                        ml: '28px',
                        mb: '6px',
                        color: comp.selectionTile.subtitleColor,
                        mt: -1,
                        pb: 1,
                      }}
                    >
                      {option.subtitle}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </>
    );
  }

  if (type === 'completion') {
    return (
      <>
        {(options || []).map((option, index) => (
          <CompletionBox
            testId={testId}
            disabled={disabled || changing}
            title={option.label}
            titleSideComponent={option.labelSideComponent}
            state='next'
            variant='edit'
            type={type}
            onAdd={async () => {
              setSelectedIndex(index);
              await onChange({ target: { value: option.value } });
            }}
            key={option.value}
            selected={
              selectedComponent === component && selectedIndex === index
            }
            sx={sx}
            innerSx={innerSx}
          >
            <Typography
              variant='bodyLarge'
              data-testid={
                testId
                  ? `${testId}-subtitle-${option.label}`
                  : `completion-box-subtitle${option.subtitle}`
              }
              sx={{ color: sys.color.onSurfaceVariant }}
            >
              {option.subtitle}
            </Typography>
          </CompletionBox>
        ))}
      </>
    );
  }

  return (
    // TODO: Checkbox
    <></>
  );
};

export default SelectionTile;
